import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Custom product main section.
 *
 * Owns the media gallery (thumbnails, prev/next, keyboard), keeps the price and
 * meta rows in sync with the selected variant, and runs the share button and the
 * "Ask a question" dialog.
 *
 * Variant syncing reuses the section HTML that the variant picker already
 * fetches — the same approach as product-price.js and product-sku.js — rather
 * than re-formatting money in JS.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} [askDialog]
 * @property {HTMLElement} [shareFeedback]
 *
 * @extends {Component<Refs>}
 */
class CustomProductMainComponent extends Component {
  /** Regions replaced wholesale when the variant changes. */
  static regions = ['price', 'meta'];

  connectedCallback() {
    super.connectedCallback();

    this.#root = this.closest('.shopify-section') ?? this;
    this.#root.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    // `close` is not in the delegated event set in component.js, so bind directly.
    this.refs.askDialog?.addEventListener('close', this.#handleDialogClose);

    // Gallery is delegated so it survives the region swaps above.
    this.addEventListener('click', this.#handleGalleryClick);
    this.addEventListener('keydown', this.#handleGalleryKeydown);

    // Thumbnail slider: keep the overflow arrows in sync with scroll position.
    this.#thumbsTrack = this.querySelector('[data-cpm-thumbs-track]');
    if (this.#thumbsTrack) {
      this.#thumbsTrack.addEventListener('scroll', this.#updateThumbArrows, { passive: true });
      window.addEventListener('resize', this.#updateThumbArrows);
      // Wait for layout before measuring overflow.
      requestAnimationFrame(this.#updateThumbArrows);
    }

    // Zoom / lightbox: deep-zoom pan needs pointer listeners the delegated
    // event set doesn't cover, and the dialog needs its own close reset.
    this.#zoomDialog = this.querySelector('[data-cpm-zoom-dialog]');
    this.#zoomImage = this.querySelector('[data-cpm-zoom-image]');
    if (this.#zoomDialog) {
      this.#zoomDialog.addEventListener('close', this.#handleZoomClose);
      this.#zoomImage?.addEventListener('pointerdown', this.#onZoomPointerDown);
      this.#zoomImage?.addEventListener('pointermove', this.#onZoomPointerMove);
      this.#zoomImage?.addEventListener('pointerup', this.#onZoomPointerUp);
      this.#zoomImage?.addEventListener('pointercancel', this.#onZoomPointerCancel);
      this.#zoomImage?.addEventListener('dblclick', this.#preventDefault);
    }

    // Pause any non-active videos on load, and honour autoplay for the first slide.
    this.#videoAutoplay = this.dataset.videoAutoplay === 'true';
    this.#syncSlideVideos();
    this.#updateZoomButton();

    if (this.dataset.openOnLoad === 'true') this.openAskDialog();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#root?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.refs.askDialog?.removeEventListener('close', this.#handleDialogClose);
    this.#thumbsTrack?.removeEventListener('scroll', this.#updateThumbArrows);
    window.removeEventListener('resize', this.#updateThumbArrows);
    this.#zoomDialog?.removeEventListener('close', this.#handleZoomClose);
    this.#zoomImage?.removeEventListener('pointerdown', this.#onZoomPointerDown);
    this.#zoomImage?.removeEventListener('pointermove', this.#onZoomPointerMove);
    this.#zoomImage?.removeEventListener('pointerup', this.#onZoomPointerUp);
    this.#zoomImage?.removeEventListener('pointercancel', this.#onZoomPointerCancel);
    this.#zoomImage?.removeEventListener('dblclick', this.#preventDefault);
    clearTimeout(this.#shareFeedbackTimer);
  }

  #preventDefault = (event) => event.preventDefault();

  /** @type {HTMLElement | null} */
  #thumbsTrack = null;

  /** @type {Element | null} */
  #root = null;

  /* ---------------------------------------------------------------- Gallery */

  get #slides() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-cpm-slide]')]);
  }

  get #thumbs() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-cpm-thumb]')]);
  }

  get activeIndex() {
    return Number(this.dataset.activeIndex ?? 0);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleGalleryClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    const thumb = target.closest?.('[data-cpm-thumb]');
    if (thumb instanceof HTMLElement) {
      this.select(Number(thumb.dataset.index));
      return;
    }

    if (target.closest?.('[data-cpm-prev]')) this.step(-1);
    if (target.closest?.('[data-cpm-next]')) this.step(1);

    // Thumbnail slider arrows scroll the track by roughly one page.
    if (target.closest?.('[data-cpm-thumb-prev]')) this.#scrollThumbs(-1);
    if (target.closest?.('[data-cpm-thumb-next]')) this.#scrollThumbs(1);

    // ---- Zoom / lightbox ----
    if (target.closest?.('[data-cpm-zoom]')) {
      this.openZoom(this.activeIndex);
      return;
    }
    // Clicking the main image opens the lightbox at that slide.
    const clickedSlide = target.closest?.('.cpm__slide-image')?.closest('[data-cpm-slide]');
    if (this.#zoomDialog && clickedSlide instanceof HTMLElement) {
      this.openZoom(Number(clickedSlide.dataset.index));
      return;
    }
    if (target.closest?.('[data-cpm-zoom-close]')) {
      this.closeZoom();
      return;
    }
    if (target.closest?.('[data-cpm-zoom-prev]')) {
      this.#zoomStep(-1);
      return;
    }
    if (target.closest?.('[data-cpm-zoom-next]')) {
      this.#zoomStep(1);
      return;
    }
    const zoomThumb = target.closest?.('[data-cpm-zoom-thumb]');
    if (zoomThumb instanceof HTMLElement) {
      this.#setZoom(Number(zoomThumb.dataset.index));
      return;
    }
    // Backdrop / empty area dismiss (not the image, arrows, or thumbs).
    const zoomStage = this.querySelector('[data-cpm-zoom-stage]');
    const zoomInner = this.querySelector('[data-cpm-zoom-inner]');
    if (target === this.#zoomDialog || target === zoomInner || target === zoomStage) {
      this.closeZoom();
    }
  };

  /**
   * Scrolls the thumbnail track by ~90% of its visible width.
   * @param {number} direction -1 for previous, 1 for next
   */
  #scrollThumbs(direction) {
    const track = this.#thumbsTrack;
    if (!track) return;
    track.scrollBy({ left: direction * track.clientWidth * 0.9, behavior: 'smooth' });
  }

  // Hide each arrow when the track can't scroll further that way. A 2px slack
  // absorbs sub-pixel rounding so an arrow doesn't linger at the extremes.
  #updateThumbArrows = () => {
    const track = this.#thumbsTrack;
    if (!track) return;

    const prev = this.querySelector('[data-cpm-thumb-prev]');
    const next = this.querySelector('[data-cpm-thumb-next]');
    const maxScroll = track.scrollWidth - track.clientWidth;
    const overflowing = maxScroll > 2;

    prev?.toggleAttribute('hidden', !overflowing || track.scrollLeft <= 2);
    next?.toggleAttribute('hidden', !overflowing || track.scrollLeft >= maxScroll - 2);
  };

  /**
   * Scrolls a thumbnail into view when its slide becomes active.
   * @param {number} index
   */
  #scrollThumbIntoView(index) {
    const thumb = this.#thumbs[index];
    thumb?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }

  /**
   * Arrow keys move through the gallery while it has focus.
   * @param {KeyboardEvent} event
   */
  #handleGalleryKeydown = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-cpm-gallery]')) return;

    // While the lightbox is open, arrows navigate the zoom (Escape closes it natively).
    if (this.#zoomDialog?.open) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.#zoomStep(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.#zoomStep(1);
      }
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.step(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.step(1);
    }
  };

  /**
   * @param {number} delta
   */
  step(delta) {
    const total = this.#slides.length;
    if (total < 2) return;
    // Wrap around in both directions.
    this.select((this.activeIndex + delta + total) % total);
  }

  /**
   * @param {number} index
   */
  select(index) {
    const slides = this.#slides;
    if (!slides.length || Number.isNaN(index)) return;

    const next = Math.max(0, Math.min(index, slides.length - 1));
    this.dataset.activeIndex = String(next);

    slides.forEach((slide, i) => {
      const active = i === next;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('hidden', !active);
    });

    this.#thumbs.forEach((thumb, i) => {
      const active = i === next;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });

    this.#scrollThumbIntoView(next);

    // Pause inactive videos, autoplay the active one if enabled, and hide the
    // zoom button on video slides (zoom is image-only).
    this.#syncSlideVideos();
    this.#updateZoomButton();
  }

  /**
   * Shows the slide whose media id matches the selected variant, if that image
   * is in the gallery. No-ops when the variant has no dedicated media.
   * @param {string|number} mediaId
   */
  #selectByMediaId(mediaId) {
    if (mediaId == null) return;
    const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index >= 0) this.select(index);
  }

  /* ------------------------------------------------------------------- Video */

  #videoAutoplay = false;

  /**
   * @param {number} index
   * @returns {boolean} whether the slide at index is an image
   */
  #isImageSlide(index) {
    const slide = this.#slides[index];
    return !slide || (slide.dataset.mediaType ?? 'image') === 'image';
  }

  /** Pause every video not on the active slide; play the active one if allowed. */
  #syncSlideVideos() {
    const active = this.activeIndex;
    this.#slides.forEach((slide, i) => {
      if (i === active) return;
      this.#pauseSlideVideo(slide);
    });

    if (this.#videoAutoplay) this.#playActiveVideo();
  }

  /**
   * Pauses native <video> and best-effort pauses external players in a slide.
   * @param {HTMLElement} slide
   */
  #pauseSlideVideo(slide) {
    const video = /** @type {HTMLVideoElement | null} */ (slide.querySelector('video'));
    if (video && !video.paused) {
      try {
        video.pause();
      } catch {
        /* ignore */
      }
    }
    // External YouTube/Vimeo iframes: best-effort pause via postMessage.
    const iframe = /** @type {HTMLIFrameElement | null} */ (slide.querySelector('iframe'));
    if (iframe?.contentWindow) {
      const src = iframe.src || '';
      try {
        if (src.includes('youtube')) {
          iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        } else if (src.includes('vimeo')) {
          iframe.contentWindow.postMessage('{"method":"pause"}', '*');
        }
      } catch {
        /* cross-origin without the JS API enabled — nothing practical to do */
      }
    }
  }

  /** Autoplay the active native video muted/inline; ignore if the browser blocks it. */
  #playActiveVideo() {
    const slide = this.#slides[this.activeIndex];
    if (!slide || (slide.dataset.mediaType ?? 'image') !== 'video') return;
    const video = /** @type {HTMLVideoElement | null} */ (slide.querySelector('video'));
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    // A rejected promise (autoplay blocked) is fine — controls stay visible.
    video.play?.().catch(() => {});
  }

  /** Zoom is image-only: hide the zoom button whenever the active slide is a video. */
  #updateZoomButton() {
    const btn = this.querySelector('[data-cpm-zoom]');
    if (!(btn instanceof HTMLElement)) return;
    btn.toggleAttribute('hidden', !this.#isImageSlide(this.activeIndex));
  }

  /* ------------------------------------------------------------- Zoom / lightbox */

  /** @type {HTMLDialogElement | null} */
  #zoomDialog = null;
  /** @type {HTMLImageElement | null} */
  #zoomImage = null;
  #zoomIndex = 0;

  // Deep-zoom (double-tap to magnify, then drag to pan) state.
  #zoomScale = 1;
  #panX = 0;
  #panY = 0;
  #dragging = false;
  #didPan = false;
  #dragStartX = 0;
  #dragStartY = 0;
  #panOriginX = 0;
  #panOriginY = 0;
  #lastTapAt = 0;
  #lastTapX = 0;
  #lastTapY = 0;

  /** Slide indices that are images — the only slides the lightbox cycles through. */
  #imageIndices() {
    return this.#slides.reduce((acc, slide, i) => {
      if ((slide.dataset.mediaType ?? 'image') === 'image') acc.push(i);
      return acc;
    }, /** @type {number[]} */ ([]));
  }

  /**
   * Opens the lightbox at a slide. No-ops for non-image slides (video/external).
   * @param {number} index
   */
  openZoom(index) {
    if (!this.#zoomDialog || !this.#isImageSlide(index)) return;
    this.#setZoom(index);
    this.#zoomDialog.showModal();
    document.documentElement.classList.add('cpm-dialog-open');
  }

  /**
   * Advances the lightbox to the next/previous IMAGE slide, wrapping.
   * @param {number} dir -1 or 1
   */
  #zoomStep(dir) {
    const images = this.#imageIndices();
    if (!images.length) return;
    const pos = images.indexOf(this.#zoomIndex);
    const nextPos = (pos + dir + images.length) % images.length;
    this.#setZoom(images[nextPos]);
  }

  closeZoom = () => {
    this.#zoomDialog?.close();
  };

  // Native close fires for the button, Escape, and our backdrop .close() alike.
  #handleZoomClose = () => {
    document.documentElement.classList.remove('cpm-dialog-open');
    this.#resetDeepZoom();
    // Leave the page gallery on whatever image the customer ended on.
    this.select(this.#zoomIndex);
  };

  /**
   * Shows a given image in the lightbox, wrapping at both ends.
   * @param {number} index
   */
  #setZoom(index) {
    const images = this.#imageIndices();
    if (!images.length) return;

    // Snap to an image slide if a non-image index somehow arrives.
    let target = index;
    if (!images.includes(target)) target = images[0];
    this.#zoomIndex = target;
    this.#resetDeepZoom();

    const slide = this.#slides[this.#zoomIndex];
    const slideImg = slide?.querySelector('img');
    if (this.#zoomImage) {
      this.#zoomImage.src = slide?.dataset.zoomSrc || slideImg?.currentSrc || slideImg?.src || '';
      this.#zoomImage.alt = slideImg?.alt || '';
    }

    // Zoom thumbs carry the SLIDE index in data-index (image slides only).
    const zoomThumbs = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-cpm-zoom-thumb]')]);
    zoomThumbs.forEach((thumb) => {
      const active = Number(thumb.dataset.index) === this.#zoomIndex;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'center' });
    });

    const counter = this.querySelector('[data-cpm-zoom-counter]');
    if (counter) counter.textContent = `${images.indexOf(this.#zoomIndex) + 1} / ${images.length}`;
  }

  #zoomStage() {
    return this.querySelector('[data-cpm-zoom-stage]');
  }

  #applyTransform() {
    if (!this.#zoomImage) return;
    this.#zoomImage.style.transform = `translate(${this.#panX}px, ${this.#panY}px) scale(${this.#zoomScale})`;
  }

  // Cap the offset at however much the scaled image overflows the stage, so an
  // edge can never be dragged inside the frame.
  #clampPan() {
    const stage = this.#zoomStage();
    if (!this.#zoomImage || !stage || this.#zoomScale <= 1) {
      this.#panX = 0;
      this.#panY = 0;
      return;
    }
    const rect = stage.getBoundingClientRect();
    const maxX = Math.max(0, (this.#zoomImage.offsetWidth * this.#zoomScale - rect.width) / 2);
    const maxY = Math.max(0, (this.#zoomImage.offsetHeight * this.#zoomScale - rect.height) / 2);
    this.#panX = Math.min(maxX, Math.max(-maxX, this.#panX));
    this.#panY = Math.min(maxY, Math.max(-maxY, this.#panY));
  }

  #resetDeepZoom() {
    this.#zoomScale = 1;
    this.#panX = 0;
    this.#panY = 0;
    this.#zoomStage()?.classList.remove('is-zoomed', 'is-panning');
    this.#applyTransform();
  }

  // Magnify about the tapped point so it stays under the cursor.
  #toggleDeepZoom(clientX, clientY) {
    if (!this.#zoomImage) return;
    if (this.#zoomScale > 1) {
      this.#resetDeepZoom();
      return;
    }
    const DEEP = 2.5;
    const rect = this.#zoomImage.getBoundingClientRect();
    const dx = clientX - (rect.left + rect.width / 2);
    const dy = clientY - (rect.top + rect.height / 2);
    this.#zoomScale = DEEP;
    this.#panX = dx * (1 - DEEP);
    this.#panY = dy * (1 - DEEP);
    this.#clampPan();
    this.#zoomStage()?.classList.add('is-zoomed');
    this.#applyTransform();
  }

  #onZoomPointerDown = (event) => {
    if (this.#zoomScale <= 1) return;
    this.#dragging = true;
    this.#didPan = false;
    this.#dragStartX = event.clientX;
    this.#dragStartY = event.clientY;
    this.#panOriginX = this.#panX;
    this.#panOriginY = this.#panY;
    this.#zoomStage()?.classList.add('is-panning');
    try {
      this.#zoomImage?.setPointerCapture(event.pointerId);
    } catch {
      /* best-effort */
    }
  };

  #onZoomPointerMove = (event) => {
    if (!this.#dragging) return;
    const dx = event.clientX - this.#dragStartX;
    const dy = event.clientY - this.#dragStartY;
    if (Math.hypot(dx, dy) > 4) this.#didPan = true;
    this.#panX = this.#panOriginX + dx;
    this.#panY = this.#panOriginY + dy;
    this.#clampPan();
    this.#applyTransform();
  };

  #endPan(event) {
    if (!this.#dragging) return;
    this.#dragging = false;
    this.#zoomStage()?.classList.remove('is-panning');
    try {
      this.#zoomImage?.releasePointerCapture(event.pointerId);
    } catch {
      /* best-effort */
    }
  }

  // One handler so a pan settles before tap detection — otherwise the release
  // at the end of a drag reads as a tap. Manual double-tap detection because
  // iOS Safari swallows dblclick for its own page zoom.
  #onZoomPointerUp = (event) => {
    const panned = this.#didPan;
    this.#endPan(event);
    if (panned) return;

    const nearLast = Math.hypot(event.clientX - this.#lastTapX, event.clientY - this.#lastTapY) < 30;
    // performance.now() avoids Date.now() (banned in this codebase's workflow
    // context) and is monotonic for interval checks.
    const now = performance.now();
    if (now - this.#lastTapAt < 300 && nearLast) {
      this.#toggleDeepZoom(event.clientX, event.clientY);
      this.#lastTapAt = 0;
    } else {
      this.#lastTapAt = now;
      this.#lastTapX = event.clientX;
      this.#lastTapY = event.clientY;
    }
  };

  #onZoomPointerCancel = (event) => this.#endPan(event);

  /* --------------------------------------------------------- Variant sync */

  /**
   * @param {CustomEvent & { promise: Promise<{ detail: any }> }} event
   */
  #handleProductSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const incoming = html.querySelector(
          `custom-product-main-component[data-section-id="${this.dataset.sectionId}"]`
        );
        if (!incoming) return;

        for (const region of CustomProductMainComponent.regions) {
          const selector = `[data-cpm-region="${region}"]`;
          const current = this.querySelector(selector);
          const next = incoming.querySelector(selector);
          if (current && next) current.replaceWith(next);
        }

        // Swap the main image to the variant's featured media when it's part of
        // the gallery. `resource` is the selected variant.
        const mediaId = detail.resource?.featured_media?.id;
        if (mediaId != null) this.#selectByMediaId(mediaId);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[custom-product-main] Event promise rejected:', error);
      });
  };

  /* ----------------------------------------------------------------- Dialog */

  openAskDialog = () => {
    const dialog = this.refs.askDialog;
    if (!dialog || dialog.open) return;
    dialog.showModal();
    document.documentElement.classList.add('cpm-dialog-open');
  };

  closeAskDialog = () => {
    this.refs.askDialog?.close();
  };

  /**
   * Closes the dialog when the backdrop — rather than the panel — is clicked.
   * @param {MouseEvent} event
   */
  handleDialogClick = (event) => {
    if (event.target === this.refs.askDialog) this.closeAskDialog();
  };

  // Also fires for Escape, which closes a native dialog without a click.
  #handleDialogClose = () => {
    document.documentElement.classList.remove('cpm-dialog-open');
  };

  /* ------------------------------------------------------------------ Share */

  handleShare = async () => {
    const url = this.dataset.productUrl;
    const title = this.dataset.productTitle;
    if (!url) return;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        // A dismissed share sheet is not a failure — only fall back on a real error.
        if (error?.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      this.#flashShareFeedback();
    } catch {
      window.prompt('Copy this link', url);
    }
  };

  #flashShareFeedback() {
    const { shareFeedback } = this.refs;
    if (!shareFeedback) return;

    shareFeedback.hidden = false;
    clearTimeout(this.#shareFeedbackTimer);
    this.#shareFeedbackTimer = setTimeout(() => {
      shareFeedback.hidden = true;
    }, 2000);
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #shareFeedbackTimer;
}

if (!customElements.get('custom-product-main-component')) {
  customElements.define('custom-product-main-component', CustomProductMainComponent);
}
